import { useState, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import TextInput from "../../elements/TextInput/TextInput";
import ImageSelector from "../../elements/ImageSelector/ui/ImageSelector";
import OpenModalButton from "../../elements/OpenModalButton/ui/OpenModalButton";
import TagInput from "../../elements/TagInput/TagInput";
import { DemandForm } from "../../modals/demand_modal/components/DemandForm";
import { createDemand } from "../../../api/demands/createDemand";
import { bulkUploadAnnouncements } from "../../../api/post/bulkUploadAnnouncements";
import ErrorModal from "../../modals/error_modal/ui/Main";

import { units } from "../constants/units";

import { t } from "i18next";

import DropdownUnit from "../../../common/components/DropdownUnit";

import warning_icon from '../../../assets/icons/WarningCircle.svg';
import CharacteristicsFields from '../../../common/components/CharacteristicsFields';
// Location dropdown utils were used for a left-side location block, now removed

const BULK_UPLOAD_MAX_FILE_BYTES = 10 * 1024 * 1024;
const BULK_UPLOAD_MAX_ROWS = 500;
const BULK_UPLOAD_ALLOWED_EXT = [".xlsx", ".csv", ".tsv"];

export function AddForm(handleImagesChange, handleVideoLinkChange, handleAnnouncementInputChange, isSubmitted, customTags, handleCustomTagsChange, handleAddTag, handleRemoveTag, tagsArray, handleSelectCategory, handleDescriptionChange, priceType, setPriceType, price, setPrice, handleVolumeChange, handlePublish, isLoading, handlePreview, selectedSubcategory, volumeUnit, announcementName, description, aiSuggestions, onSuggestionClick, onAIGenerate, onUpdateSuggestions, activeTab, setActiveTab, selectedCategory, jsonData, setJsonData) {
    // Demand form state
    const [demandTitle, setDemandTitle] = useState('');
    const [demandDescription, setDemandDescription] = useState('');
    const [demandMaxPrice, setDemandMaxPrice] = useState('');
    const [demandSelectedCategory, setDemandSelectedCategory] = useState('');
    const [demandSelectedSubcategory, setDemandSelectedSubcategory] = useState('');
    const [demandKeywords, setDemandKeywords] = useState('');
    const [demandKeywordsArray, setDemandKeywordsArray] = useState([]);
    const [demandMeasurementUnit, setDemandMeasurementUnit] = useState(null);
    const [demandQuantity, setDemandQuantity] = useState('');
    const [demandIsSubmitted, setDemandIsSubmitted] = useState(false);
    const [demandIsLoading, setDemandIsLoading] = useState(false);
    const [showDemandErrorModal, setShowDemandErrorModal] = useState(false);
    const [demandErrorMessage, setDemandErrorMessage] = useState('');
    const [demandJsonData, setDemandJsonData] = useState({});

    // Bulk upload state
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkTableData, setBulkTableData] = useState(null); // { headers: string[], rows: string[][] }
    const [bulkParseError, setBulkParseError] = useState(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [bulkError, setBulkError] = useState(null);
    const bulkFileInputRef = useRef(null);

    const validateDemandForm = () => {
        const errors = [];
        if (!demandTitle.trim()) errors.push(t('demand_title_required'));
        if (!demandDescription.trim()) errors.push(t('description_required'));
        if (!demandSelectedCategory || !demandSelectedSubcategory) errors.push(t('category_required'));
        if (!demandMeasurementUnit) errors.push(t('measurement_unit_required'));
        if (!demandQuantity || parseFloat(demandQuantity) <= 0 || isNaN(parseFloat(demandQuantity))) errors.push(t('quantity_required'));
        return errors;
    };

    const handleDemandSubmit = async () => {
        setDemandIsSubmitted(true);
        const errors = validateDemandForm();
        
        if (errors.length > 0) {
            setDemandErrorMessage(errors.join(', '));
            setShowDemandErrorModal(true);
            return;
        }

        setDemandIsLoading(true);

        try {
            const accessToken = localStorage.getItem("access_token");
            if (!accessToken) {
                throw new Error(t('authentication_required'));
            }

            let finalKeywordsArray = demandKeywordsArray.length > 0 ? demandKeywordsArray : [];
            if (finalKeywordsArray.length === 0 && demandTitle.trim()) {
                finalKeywordsArray = demandTitle.trim().split(/\s+/).filter(word => word.length > 2);
            }

            const unitMapping = {
                'piece': 'PIECE',
                'kilogram': 'KILOGRAM',
                'ton': 'TON',
                'liter': 'LITER',
                'hectare': 'HECTARE',
                'square_meter': 'SQUARE_METER',
                'cubic_meter': 'CUBIC_METER',
                'bag': 'BAG',
                'centner': 'CENTNER',
                'head': 'HEAD',
                'pair': 'PAIR',
                'meter': 'METER',
                'box': 'BOX'
            };
            const unitForServer = unitMapping[demandMeasurementUnit] || demandMeasurementUnit?.toUpperCase() || 'PIECE';

            const demandData = {
                title: demandTitle.trim(),
                description: demandDescription.trim(),
                max_price: demandMaxPrice ? parseFloat(demandMaxPrice) : null,
                currency: "KZT",
                category_id: parseInt(demandSelectedCategory),
                subcategory_id: parseInt(demandSelectedSubcategory),
                user_location_id: null,
                keywords: finalKeywordsArray,
                measurement_unit: unitForServer,
                quantity: parseFloat(demandQuantity),
                json_data: demandJsonData && Object.keys(demandJsonData).length > 0 ? demandJsonData : undefined,
            };

            const response = await createDemand(demandData, accessToken);
            
            if (response.demand_id) {
                // Reset form
                setDemandTitle('');
                setDemandDescription('');
                setDemandMaxPrice('');
                setDemandSelectedCategory('');
                setDemandSelectedSubcategory('');
                setDemandKeywords('');
                setDemandKeywordsArray([]);
                setDemandMeasurementUnit(null);
                setDemandQuantity('');
                setDemandIsSubmitted(false);
                alert(t('demand_created_successfully'));
            }
        } catch (error) {
            console.error("Error creating demand:", error);
            setDemandErrorMessage(error.message || t('error_creating_demand'));
            setShowDemandErrorModal(true);
        } finally {
            setDemandIsLoading(false);
        }
    };

    const buildFileFromTable = (tableData, originalFileName) => {
        const ext = (originalFileName?.split(".").pop() || "csv").toLowerCase();
        const { headers, rows } = tableData;
        if (ext === "xlsx" || ext === "xls") {
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
            const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
            return new File([buf], originalFileName || "bulk.xlsx", { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        }
        const sep = ext === "tsv" ? "\t" : ",";
        const escape = (v) => (String(v).includes(sep) || String(v).includes('"') ? `"${String(v).replace(/"/g, '""')}"` : String(v));
        const csv = [headers.map(escape).join(sep), ...rows.map((r) => r.map(escape).join(sep))].join("\n");
        const mime = ext === "tsv" ? "text/tab-separated-values" : "text/csv";
        return new File([csv], originalFileName || "bulk.csv", { type: mime });
    };

    useEffect(() => {
        if (!bulkFile) {
            setBulkTableData(null);
            setBulkParseError(null);
            return;
        }
        setBulkParseError(null);
        const ext = (bulkFile.name?.split(".").pop() || "").toLowerCase();
        const parse = async () => {
            if (ext === "xlsx" || ext === "xls") {
                const ab = await bulkFile.arrayBuffer();
                const wb = XLSX.read(ab, { type: "array" });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                if (!sheet) return { headers: [], rows: [] };
                const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
                const headers = (aoa[0] || []).map((h) => String(h ?? ""));
                const rows = (aoa.slice(1) || []).map((row) => headers.map((_, i) => String(row[i] ?? "")));
                return { headers, rows };
            }
            const text = await bulkFile.text();
            const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
            if (lines.length === 0) return { headers: [], rows: [] };
            const sep = ext === "tsv" ? "\t" : ",";
            const headers = lines[0].split(sep).map((c) => c.trim().replace(/^"|"$/g, ""));
            const rows = lines.slice(1).map((line) => {
                const cells = line.split(sep).map((c) => c.trim().replace(/^"|"$/g, ""));
                return headers.map((_, i) => cells[i] ?? "");
            });
            return { headers, rows };
        };
        parse()
            .then((data) => {
                setBulkTableData(data);
                if (data.rows.length > BULK_UPLOAD_MAX_ROWS) {
                    setBulkParseError(t("bulk_upload_too_many_rows"));
                }
            })
            .catch((err) => {
                setBulkParseError(err?.message || t("bulk_upload_read_error"));
                setBulkTableData(null);
            });
    }, [bulkFile]);

    const handleBulkFileChange = (e) => {
        const file = e.target.files?.[0];
        setBulkResult(null);
        setBulkError(null);
        setBulkTableData(null);
        setBulkParseError(null);
        if (!file) {
            setBulkFile(null);
            return;
        }
        const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
        if (!BULK_UPLOAD_ALLOWED_EXT.includes(ext)) {
            setBulkParseError(t("bulk_upload_invalid_format"));
            setBulkFile(null);
            e.target.value = "";
            return;
        }
        if (file.size > BULK_UPLOAD_MAX_FILE_BYTES) {
            setBulkParseError(t("bulk_upload_file_too_large"));
            setBulkFile(null);
            e.target.value = "";
            return;
        }
        setBulkFile(file);
    };

    const handleBulkRemoveFile = () => {
        setBulkFile(null);
        setBulkTableData(null);
        setBulkParseError(null);
        setBulkResult(null);
        setBulkError(null);
        if (bulkFileInputRef.current) bulkFileInputRef.current.value = "";
    };

    const handleBulkTableCellChange = (rowIndex, colIndex, value) => {
        setBulkTableData((prev) => {
            if (!prev || !prev.rows[rowIndex]) return prev;
            const nextRows = prev.rows.map((r, i) => (i === rowIndex ? r.map((c, j) => (j === colIndex ? value : c)) : r));
            return { ...prev, rows: nextRows };
        });
    };

    const handleBulkUpload = async () => {
        if (!bulkFile) return;
        const rowCount = bulkTableData?.rows?.length ?? 0;
        if (rowCount > BULK_UPLOAD_MAX_ROWS) {
            setBulkError(t("bulk_upload_too_many_rows"));
            return;
        }
        if (bulkFile.size > BULK_UPLOAD_MAX_FILE_BYTES) {
            setBulkError(t("bulk_upload_file_too_large"));
            return;
        }
        const token = localStorage.getItem("access_token");
        if (!token) {
            setBulkError(t("authentication_required"));
            return;
        }
        setBulkLoading(true);
        setBulkError(null);
        setBulkResult(null);
        try {
            const fileToSend = bulkTableData?.headers?.length ? buildFileFromTable(bulkTableData, bulkFile.name) : bulkFile;
            const result = await bulkUploadAnnouncements(fileToSend, token);
            setBulkResult(result);
        } catch (err) {
            setBulkError(err.message || t("error_creating_demand"));
        } finally {
            setBulkLoading(false);
        }
    };

    return <div className="w-full lg:w-[62%] rounded-[10px] mr-0 lg:mr-5 box-border">
        <h2 className="text-[36px] font-bold text-[#222] mb-0 bg-[var(--background-white-color)] rounded-t-[10px] py-5 px-5 mt-0">{t('announcement')}</h2>
        <div className="flex gap-2.5 flex-col md:flex-row bg-[var(--background-white-color)] px-5 pb-5 rounded-b-[10px] mb-2.5">
            <button
                className={`py-2.5 px-5 border border-[var(--primary-color)] rounded-lg cursor-pointer text-base font-medium transition-all duration-200 w-full md:w-auto text-center ${
                    activeTab === 'advertisement'
                        ? 'bg-[var(--primary-color)] text-white border-[var(--primary-color)]'
                        : 'bg-[var(--background-white-color)] text-[var(--text-color)] hover:bg-[rgba(20,127,38,0.05)]'
                }`}
                onClick={() => setActiveTab('advertisement')}
            >
                {t('add_advertisement')}
            </button>
            <button
                className={`py-2.5 px-5 border border-[var(--primary-color)] rounded-lg cursor-pointer text-base font-medium transition-all duration-200 w-full md:w-auto text-center ${
                    activeTab === 'demand'
                        ? 'bg-[var(--primary-color)] text-white border-[var(--primary-color)]'
                        : 'bg-[var(--background-white-color)] text-[var(--text-color)] hover:bg-[rgba(20,127,38,0.05)]'
                }`}
                onClick={() => setActiveTab('demand')}
            >
                {t('create_demand')}
            </button>
            <button
                className={`py-2.5 px-5 border border-[var(--primary-color)] rounded-lg cursor-pointer text-base font-medium transition-all duration-200 w-full md:w-auto text-center ${
                    activeTab === 'bulk'
                        ? 'bg-[var(--primary-color)] text-white border-[var(--primary-color)]'
                        : 'bg-[var(--background-white-color)] text-[var(--text-color)] hover:bg-[rgba(20,127,38,0.05)]'
                }`}
                onClick={() => setActiveTab('bulk')}
            >
                {t('bulk_upload_tab')}
            </button>
        </div>
        {activeTab === 'bulk' ? (
            <div className="flex flex-col gap-4 bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5">
                <p className="text-sm text-[var(--text-color)] m-0 leading-normal whitespace-pre-line">
                    {t("bulk_upload_help")}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={bulkFileInputRef}
                        type="file"
                        accept=".xlsx,.csv,.tsv"
                        onChange={handleBulkFileChange}
                        className="absolute w-0.1 h-0.1 opacity-0 overflow-hidden -z-[1]"
                        id="bulk_upload_file"
                    />
                    <label htmlFor="bulk_upload_file" className="cursor-pointer inline-block py-2.5 px-4 rounded-lg border border-[var(--primary-color)] text-[var(--primary-color)] text-sm transition-colors duration-200 hover:bg-[var(--primary-color)] hover:text-[var(--background-white-color)]">
                        {bulkFile ? t('bulk_upload_select_file') + ' (' + bulkFile.name + ')' : t('bulk_upload_select_file')}
                    </label>
                    {bulkFile && (
                        <button
                            type="button"
                            className="py-2.5 px-4 rounded-lg border border-[#ccc] bg-transparent text-[var(--text-color)] text-base cursor-pointer hover:bg-[#f5f5f5] hover:border-[#999] disabled:opacity-60 disabled:cursor-not-allowed"
                            onClick={handleBulkRemoveFile}
                            disabled={bulkLoading}
                        >
                            {t('bulk_upload_remove_file')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="py-2.5 px-4 rounded-[10px] border-none cursor-pointer text-base bg-[var(--primary-color)] text-white w-full h-[50px] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                        onClick={handleBulkUpload}
                        disabled={!bulkFile || bulkLoading || !!bulkParseError}
                    >
                        {bulkLoading ? <div className="flex justify-center items-center w-full"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div></div> : t('bulk_upload_upload_btn')}
                    </button>
                </div>
                {(bulkParseError || bulkError) && <div className="text-[#c00] text-sm mt-2">{bulkParseError || bulkError}</div>}
                {bulkTableData && bulkTableData.headers.length > 0 && (
                    <div className="w-full overflow-x-auto overflow-y-auto max-h-[360px] mt-3 border border-[#e0e0e0] rounded-lg bg-white">
                        <table className="w-full border-collapse text-[13px]">
                            <thead>
                                <tr>
                                    {bulkTableData.headers.map((h, i) => (
                                        <th key={i} className="border border-[#e0e0e0] py-1.5 px-2 text-left whitespace-nowrap bg-[#f5f5f5] font-semibold sticky top-0 z-[1]">{h || `Col ${i + 1}`}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {bulkTableData.rows.map((row, ri) => (
                                    <tr key={ri}>
                                        {bulkTableData.headers.map((_, ci) => (
                                            <td key={ci} className="border border-[#e0e0e0] py-1.5 px-2 text-left whitespace-nowrap bg-white">
                                                <input
                                                    type="text"
                                                    className="w-full min-w-[80px] max-w-[200px] py-1 px-1.5 border-none text-[13px] outline-none bg-transparent focus:bg-[#f0f9f0]"
                                                    value={row[ci] ?? ""}
                                                    onChange={(e) => handleBulkTableCellChange(ri, ci, e.target.value)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {bulkResult && (
                    <div className="mt-4 p-3 bg-[#f0f9f0] rounded-lg border border-[#c8e6c9]">
                        <div className="font-semibold text-[var(--primary-color)] mb-2">{t('bulk_upload_success')}</div>
                        <div className="flex gap-4 mb-2">
                            <span>{t('bulk_upload_created_count')}: {bulkResult.created_count ?? 0}</span>
                            <span>{t('bulk_upload_error_count')}: {bulkResult.error_count ?? 0}</span>
                        </div>
                        {bulkResult.errors?.length > 0 && (
                            <div className="text-[13px]">
                                <strong>{t('bulk_upload_errors')}:</strong>
                                <ul className="mt-1 ml-5 p-0">
                                    {(bulkResult.errors || []).map((e, i) => (
                                        <li key={i} className="my-0.5">{t('bulk_upload_row')} {e.row}: {e.error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>
        ) : activeTab === 'advertisement' ? (
        <>
        <div className="bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5">
            <ImageSelector onImagesChange={handleImagesChange} />
            <hr className="divider" />
            <div className="mt-3.5">
                <TextInput
                    isRequired={false}
                    onInputChange={handleVideoLinkChange}
                    labelText={t('video_link')}
                    placeholder={t('video_link')}
                    type="url"
                    storageKey="videoLink"
                />
            </div>
        </div>

        <div className="bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5">
            <TextInput
                isRequired={true}
                onInputChange={handleAnnouncementInputChange}
                isSubmitted={isSubmitted}
                errorText={t('required_text')}
                labelText={t('announcement_title')}
                placeholder={t('announcement_title')}
                type="text"
                storageKey="announcementName"
            />
            <button 
                className="bg-[var(--primary-color)] text-white border-none rounded-lg py-2.5 px-4 text-sm font-medium cursor-pointer flex items-center gap-2 mt-3 transition-all duration-300 hover:bg-[var(--hover-color)] hover:-translate-y-px disabled:bg-[#ccc] disabled:cursor-not-allowed disabled:opacity-60 w-fit"
                onClick={() => onAIGenerate(announcementName)}
                disabled={!announcementName || announcementName.trim().length < 3}
                title={t('aiGenerateTooltip')}
            >
                🤖 {t('aiGenerate')}
            </button>
            {aiSuggestions && (aiSuggestions.description || aiSuggestions.keyword) && (
                <div className="bg-[#f8f9fa] border border-[#e9ecef] rounded-lg p-4 mt-3">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-semibold text-[var(--primary-color)] m-0">{t('aiSuggestions')}</h4>
                        <button 
                            className="bg-[var(--primary-color)] text-white border-none rounded py-1.5 px-3 text-xs font-medium cursor-pointer transition-all duration-200 hover:bg-[var(--hover-color)] hover:-translate-y-px"
                            onClick={() => onUpdateSuggestions(announcementName)}
                            title={t('updateSuggestions')}
                        >
                            {t('update')}
                        </button>
                    </div>
                    {aiSuggestions.keyword && (
                        <div className="mb-3 last:mb-0">
                            <label className="text-xs font-medium text-[#666] block mb-1">{t('announcement_title')}:</label>
                            <div className="bg-white border border-[#ddd] rounded-md py-2 px-3 cursor-pointer transition-colors duration-200 text-sm leading-snug hover:bg-[var(--primary-color)] hover:text-white hover:border-[var(--primary-color)]" onClick={() => onSuggestionClick('keyword', aiSuggestions.keyword, aiSuggestions)}>
                                {aiSuggestions.keyword}
                            </div>
                        </div>
                    )}
                    {aiSuggestions.description && (
                        <div className="mb-3 last:mb-0">
                            <label className="text-xs font-medium text-[#666] block mb-1">{t('description')}:</label>
                            <div className="bg-white border border-[#ddd] rounded-md py-2 px-3 cursor-pointer transition-colors duration-200 text-sm leading-snug hover:bg-[var(--primary-color)] hover:text-white hover:border-[var(--primary-color)]" onClick={() => onSuggestionClick('description', aiSuggestions.description, aiSuggestions)}>
                                {aiSuggestions.description}
                            </div>
                        </div>
                    )}
                    {aiSuggestions.tags && aiSuggestions.tags.length > 0 && (
                        <div className="mb-3 last:mb-0">
                            <label className="text-xs font-medium text-[#666] block mb-1">{t('tags')}:</label>
                            <div className="flex flex-wrap gap-1.5">
                                {aiSuggestions.tags.map((tag, index) => (
                                    <span 
                                        key={index} 
                                        className="bg-white border border-[#ddd] rounded-2xl py-1 px-3 cursor-pointer transition-colors duration-200 text-xs text-[#666] hover:bg-[var(--primary-color)] hover:text-white hover:border-[var(--primary-color)]"
                                        onClick={() => onSuggestionClick('tag', tag, aiSuggestions)}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {(aiSuggestions.category || (aiSuggestions.category_id && aiSuggestions.subcategory_id)) && (
                        <div className="mb-3 last:mb-0">
                            <label className="text-xs font-medium text-[#666] block mb-1">{t('category')}:</label>
                            <div className="bg-white border border-[#ddd] rounded-md py-2 px-3 cursor-pointer transition-colors duration-200 text-sm leading-snug hover:bg-[var(--primary-color)] hover:text-white hover:border-[var(--primary-color)]" onClick={() => onSuggestionClick('category', aiSuggestions.category || '', aiSuggestions)}>
                                {aiSuggestions.category || ''}
                                {aiSuggestions.subcategory && ` > ${aiSuggestions.subcategory}`}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
        <div className="bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5">
            <TagInput
                value={customTags}
                onChange={handleCustomTagsChange}
                onAddTag={handleAddTag}
                onRemoveTag={handleRemoveTag}
                tags={tagsArray}
                isSubmitted={isSubmitted}
                isRequired={true}
                errorText={t('required_text')}
                placeholder={t('enter_keyword')} />
        </div>
        <div className="bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5">
            <label className="text_input_label">{t('select_category')}</label>
            <div className="input_header">
                {isSubmitted && !selectedSubcategory && (
                    <span className="error_text">{t('required_text')}</span>
                )}

                {isSubmitted && !selectedSubcategory && (
                    <img src={warning_icon} alt='warning' />
                )}
            </div>
            <div style={{ marginTop: 14 }}>
                <OpenModalButton type="category" onSelect={handleSelectCategory} />
            </div>
        </div>

        {selectedCategory && (
            <CharacteristicsFields
                categoryId={selectedCategory}
                values={jsonData || {}}
                onChange={setJsonData}
            />
        )}

        <div className="bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5">
            <TextInput
                isRequired={true}
                onInputChange={handleDescriptionChange}
                isSubmitted={isSubmitted}
                errorText={t('required_text')}
                labelText={t('description')}
                multiline={true}
                rows={8}
                placeholder={t('description_placeholder')}
                maxLength={1000}
                storageKey="description"
                value={description} />
        </div>

        <div className="bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5">
            <label className="text_input_label">{t('price')}</label>
            <div className="flex gap-2.5 flex-row flex-wrap w-full my-3.5">
                <button
                    className={`flex-1 h-[40px] rounded-[10px] text-base min-w-0 md:min-w-[200px] border transition-colors ${
                        priceType === "fixed"
                            ? "bg-[var(--primary-color)] text-white border-[var(--primary-color)]"
                            : "bg-[var(--background-white-color)] text-[var(--text-color)] border-[#EDEDED]"
                    }`}
                    onClick={() => setPriceType("fixed")}
                >
                    {t('fixed_price')}
                </button>
                <button
                    className={`flex-1 h-[40px] rounded-[10px] text-base min-w-0 md:min-w-[200px] border transition-colors ${
                        priceType === "negotiable"
                            ? "bg-[var(--primary-color)] text-white border-[var(--primary-color)]"
                            : "bg-[var(--background-white-color)] text-[var(--text-color)] border-[#EDEDED]"
                    }`}
                    onClick={() => setPriceType("negotiable")}
                >
                    {t('negotiable')}
                </button>
            </div>
            <div className="input_header">
                {isSubmitted && !price && (
                    <span className="error_text">{t('required_text')}</span>
                )}

                {isSubmitted && !price && (
                    <img src={warning_icon} alt="warning" />
                )}
            </div>
            <div className="flex flex-row gap-2.5 items-center w-full">
                <input
                    type="number"
                    className="phone_input price_input flex-1 min-w-0"
                    placeholder={t('enter_price')}
                    value={price && price !== 'None' && price !== 'null' ? price : ''}
                    onChange={(e) => setPrice(e.target.value)} />
                <DropdownUnit
                    text={t('volume_production')}
                    options={units}
                    selected={volumeUnit}
                    onOptionSelect={handleVolumeChange}
                    style={{ width: "200px" }}
                />
            </div>
        </div>

        <div className="flex flex-row gap-2.5 bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5 md:flex hidden">
            <button
                className="py-2.5 px-4 rounded-[10px] border-none cursor-pointer text-base bg-[var(--primary-color)] text-white w-full h-[40px] flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handlePublish}
                disabled={isLoading}
            >
                {isLoading ? <div className="flex justify-center items-center w-full"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div></div> : t("publish")}
            </button>
            <button
                className="py-2.5 px-4 rounded-[10px] border border-[var(--primary-color)] bg-transparent text-[var(--text-color)] cursor-pointer text-base ml-2.5 w-full h-[40px] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isLoading}
                onClick={handlePreview}
            >
                {t('preview')}
            </button>
        </div>
        </>
        ) : (
            <div className="w-full">
                <DemandForm
                    title={demandTitle}
                    setTitle={setDemandTitle}
                    description={demandDescription}
                    setDescription={setDemandDescription}
                    maxPrice={demandMaxPrice}
                    setMaxPrice={setDemandMaxPrice}
                    selectedCategory={demandSelectedCategory}
                    setSelectedCategory={setDemandSelectedCategory}
                    selectedSubcategory={demandSelectedSubcategory}
                    setSelectedSubcategory={setDemandSelectedSubcategory}
                    keywords={demandKeywords}
                    setKeywords={setDemandKeywords}
                    keywordsArray={demandKeywordsArray}
                    setKeywordsArray={setDemandKeywordsArray}
                    measurementUnit={demandMeasurementUnit}
                    setMeasurementUnit={setDemandMeasurementUnit}
                    quantity={demandQuantity}
                    setQuantity={setDemandQuantity}
                    isSubmitted={demandIsSubmitted}
                    jsonData={demandJsonData}
                    setJsonData={setDemandJsonData}
                />
                <div className="flex flex-row gap-2.5 bg-[var(--background-white-color)] rounded-[10px] p-5 mt-2.5 md:flex hidden">
                    <button
                        className="py-2.5 px-4 rounded-[10px] border-none cursor-pointer text-base bg-[var(--primary-color)] text-white w-full h-[40px] flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleDemandSubmit}
                        disabled={demandIsLoading}
                    >
                        {demandIsLoading ? <div className="flex justify-center items-center w-full"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div></div> : t("create_demand")}
                    </button>
                </div>
                <ErrorModal 
                    showLogoutModal={showDemandErrorModal} 
                    setShowLogoutModal={setShowDemandErrorModal} 
                    message={demandErrorMessage} 
                />
            </div>
        )}
    </div>;
}